import React from 'react';
import {
  Users,
  Search,
  UserPlus,
  Mail,
  MessageCircle,
  Star,
  TrendingUp,
  Sparkles,
  ChevronRight,
  Heart,
  UserCheck,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Send,
  Inbox,
  History,
  Zap,
  MapPin,
  Building,
  GraduationCap,
  Briefcase,
  Link2,
  MoreHorizontal,
  Grid3X3,
  Compass,
  Bell,
  Filter,
  ArrowUpRight,
  Award,
  Handshake,
} from 'lucide-react';
import './CollaboratorsMockupContent.css';

const CollaboratorsMockupContent = ({ 
  visibleItems, 
  animationPhase,
  activeInteriorTab = 'network', // 'network', 'discover', 'connections', 'requests'
  onTabClick = () => {}, // Handler for tab clicks
}) => {
  // Avatar colors for variety
  const avatarColors = [
    'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', // blue
    'linear-gradient(135deg, #10b981 0%, #34d399 100%)', // green
    'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', // amber
    'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)', // cyan
    'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', // pink
    'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)', // teal
    'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', // red
    'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)', // indigo
  ];

  // People you may know data
  const peopleYouMayKnow = [
    { id: 1, name: 'Elena Voss', initials: 'EV', role: 'Postdoc', field: 'Biomedical Engineering', institution: 'MIT', mutual: 5, followers: 4, color: 0, verified: true },
    { id: 2, name: 'Callum Fraser', initials: 'CF', role: 'Student', field: 'Medicine', institution: 'UCL', mutual: 2, followers: 1, color: 1, verified: true },
    { id: 3, name: 'Sadiah', initials: 'SA', role: 'Researcher', field: 'Engineering', institution: 'LUMS', mutual: 0, followers: 4, color: 2, verified: true },
    { id: 4, name: 'Yuki Tanabe', initials: 'YT', role: 'Researcher', field: 'Neuroscience', institution: 'MIT', mutual: 0, followers: 4, color: 3 },
    { id: 5, name: 'Mehran Akhtar', initials: 'MA', role: 'Researcher', field: 'Natural Sciences', institution: 'MIT', mutual: 0, followers: 3, color: 4 },
    { id: 6, name: 'Lucas Bergmann', initials: 'LB', role: 'Student', field: 'Computer Science', institution: 'TU Munich', mutual: 0, followers: 3, color: 5 },
  ];

  // Trending researchers data
  const trendingResearchers = [
    { id: 1, name: 'Ingrid Svensson', initials: 'IS', role: 'Medical AI Researcher', field: 'Medicine', followers: 13, following: true, color: 1 },
    { id: 2, name: 'Prof. Wei Zhang', initials: 'WZ', role: 'Professor', field: 'Engineering', followers: 6, following: true, color: 4 },
    { id: 3, name: 'Hamza Qureshi', initials: 'HQ', role: 'PhD Student', field: 'Biophysics', followers: 4, following: false, color: 0 },
    { id: 4, name: 'Declan O\'Brien', initials: 'DO', role: 'Entrepreneur', field: 'Engineering', followers: 3, following: true, color: 3 },
    { id: 5, name: 'Amara Osei', initials: 'AO', role: 'Undergraduate', field: 'Computer Science', followers: 2, following: false, color: 5 },
  ];

  // Recommended for you (AI picks)
  const recommendedForYou = [
    { id: 1, name: 'Clara Whitfield', initials: 'CW', role: 'Postdoc', field: 'Neuroscience', reason: 'Followed by 3 people you follow', color: 0, aiPick: true },
    { id: 2, name: 'Sadiah', initials: 'SA', role: 'Researcher', field: 'Engineering', reason: 'Based on your activity', color: 2, aiPick: true, verified: true },
    { id: 3, name: 'Lena Hoffmann', initials: 'LH', role: 'Researcher', field: 'Medicine', reason: 'Based on your activity', color: 6, aiPick: true },
    { id: 4, name: 'Naveed Iqbal', initials: 'NI', role: 'Entrepreneur', field: 'Engineering', reason: '1 shared interests', color: 3, aiPick: true },
    { id: 5, name: 'Marie Dubois', initials: 'MD', role: 'Student', field: 'Social Sciences', reason: 'Based on your activity', color: 5, aiPick: true },
  ];

  // Discover researchers
  const discoverResearchers = [
    { id: 1, name: 'Tobias Richter', initials: 'TR', role: 'Postdoc', field: 'Engineering', rating: 3.5, bio: 'Collaborative researcher focused on solving real-world challenges...', tags: ['Space Technology', 'Physics', 'Materials Science'], followers: 3, openToCollab: true, color: 1, hasImage: true },
    { id: 2, name: 'Nkechi Adeyemi', initials: 'NA', role: 'Researcher', field: 'Data Science', rating: 4.8, bio: 'Experienced researcher with a passion for discovery and innovation...', tags: ['Space Technology', 'Physics', 'Materials Science'], followers: 3, openToCollab: true, color: 5 },
    { id: 3, name: 'Kamran Raza', initials: 'KR', role: 'Researcher', field: 'Medicine', rating: 4.2, bio: 'Passionate researcher working on innovative solutions to complex problems...', tags: ['Engineering', 'Robotics', 'IoT'], followers: 4, openToCollab: true, color: 3, verified: true },
    { id: 4, name: 'Fiona Gallagher', initials: 'FG', role: 'Researcher', field: 'Engineering', rating: 4.4, bio: 'Innovative thinker with a strong background in research and development...', tags: ['Data Science', 'Healthcare', 'Education'], followers: 4, openToCollab: true, color: 2, verified: true },
  ];

  // Connections (Following)
  const connections = [
    { id: 1, name: 'Jianwei Xu', initials: 'JX', role: 'Researcher', field: 'Engineering', followers: 3, color: 4 },
    { id: 2, name: 'Ingrid Svensson', initials: 'IS', role: 'Medical AI Researcher', field: 'Medicine', followers: 13, color: 1, hasImage: true },
    { id: 3, name: 'Prof. Wei Zhang', initials: 'WZ', role: 'Professor', field: 'Engineering', followers: 6, rating: 4.9, color: 0, verified: true },
    { id: 4, name: 'Hamza Qureshi', initials: 'HQ', role: 'PhD Student', field: 'Biophysics', followers: 3, rating: 3.5, color: 3, verified: true },
    { id: 5, name: 'Elif Demir', initials: 'ED', role: 'Researcher', field: 'Bioinformatics', followers: 3, rating: 4.8, color: 5 },
  ];

  // Requests data - Professional collaboration requests
  const requestsData = [
    { id: 1, name: 'Dr. Lena Hoffmann', initials: 'LH', role: 'AI Researcher', type: 'received', status: 'pending', room: 'Neural Network Lab', message: 'Hi! I saw your work on transformer architectures. Would love to collaborate on our attention mechanism research.', date: '01/28/2026', color: 0, verified: true },
    { id: 2, name: 'Prof. Idris Malik', initials: 'IM', role: 'Professor', type: 'sent', status: 'accepted', room: 'Climate AI Initiative', message: 'Interested in joining your climate modeling project. I have 3 years of experience in environmental data analysis.', response: "Welcome aboard! Let's schedule an onboarding call.", date: '01/25/2026', color: 1, verified: true },
    { id: 3, name: 'Clara Whitfield', initials: 'CW', role: 'PhD Candidate', type: 'received', status: 'pending', room: 'Quantum Computing Lab', message: 'Your recent paper on quantum error correction was inspiring! Looking to discuss potential collaboration opportunities.', date: '01/24/2026', color: 4 },
    { id: 4, name: 'Dr. Tobias Richter', initials: 'TR', role: 'Research Lead', type: 'sent', status: 'accepted', room: 'Drug Discovery AI', message: 'Requesting to join your ML-driven pharmaceutical research team. My expertise is in molecular modeling.', response: "Great fit! Your background is exactly what we need.", date: '01/22/2026', color: 3, verified: true },
    { id: 5, name: 'Naima Farooq', initials: 'NF', role: 'Data Scientist', type: 'received', status: 'pending', room: '', message: 'Would like to connect and discuss potential research collaborations in NLP and healthcare applications.', date: '01/20/2026', color: 2 },
  ];

  // Tab configuration
  const mainTabs = [
    { key: 'network', icon: Grid3X3, label: 'My Network' },
    { key: 'discover', icon: Search, label: 'Discover' },
    { key: 'connections', icon: Users, label: 'Connections' },
    { key: 'requests', icon: Mail, label: 'Requests' },
  ];

  // Render My Network Tab
  const renderNetworkTab = () => (
    <div className="collab-network">
      {/* People You May Know Section */}
      <div className={`collab-section ${visibleItems >= 1 ? 'visible' : ''}`}>
        <div className="collab-section-header">
          <div className="collab-section-title">
            <Users size={12} />
            <span>People you may know</span>
            <span className="collab-section-count">31</span>
          </div>
          <button className="collab-show-all">
            Show all <ChevronRight size={10} />
          </button>
        </div>
        <div className="collab-people-grid">
          {peopleYouMayKnow.slice(0, 6).map((person, idx) => (
            <div 
              key={person.id} 
              className={`collab-person-card ${idx < visibleItems ? 'visible' : ''}`}
              style={{ transitionDelay: `${idx * 60}ms` }}
            >
              <div className="collab-person-avatar" style={{ background: avatarColors[person.color] }}>
                <span>{person.initials}</span>
                {person.verified && (
                  <div className="collab-verified-badge">
                    <CheckCircle2 size={8} />
                  </div>
                )}
              </div>
              <h4 className="collab-person-name">{person.name}</h4>
              <span className="collab-person-role">{person.role}</span>
              <span className="collab-person-field">{person.field}</span>
              {person.institution && (
                <span className="collab-person-institution">
                  <Building size={8} />
                  {person.institution}
                </span>
              )}
              {person.mutual > 0 && (
                <span className="collab-mutual-badge">{person.mutual} mutual</span>
              )}
              <div className="collab-person-stats">
                <Users size={9} />
                <span>{person.followers}</span>
              </div>
              <button className="collab-follow-btn">Follow</button>
            </div>
          ))}
        </div>
      </div>

      {/* Trending Researchers Section */}
      <div className={`collab-section ${visibleItems >= 2 ? 'visible' : ''}`} style={{ transitionDelay: '200ms' }}>
        <div className="collab-section-header">
          <div className="collab-section-title">
            <TrendingUp size={12} />
            <span className="collab-trending-icon">🔥</span>
            <span>Trending Researchers</span>
            <span className="collab-section-count">100</span>
          </div>
          <button className="collab-show-all">
            Show all <ChevronRight size={10} />
          </button>
        </div>
        <div className="collab-people-grid">
          {trendingResearchers.slice(0, 5).map((person, idx) => (
            <div 
              key={person.id} 
              className={`collab-person-card ${idx < visibleItems ? 'visible' : ''}`}
              style={{ transitionDelay: `${(idx + 6) * 60}ms` }}
            >
              <div className="collab-person-avatar" style={{ background: avatarColors[person.color] }}>
                <span>{person.initials}</span>
              </div>
              <h4 className="collab-person-name">{person.name}</h4>
              <span className="collab-person-role">{person.role}</span>
              <span className="collab-person-field">{person.field}</span>
              <div className="collab-person-stats">
                <Users size={9} />
                <span>{person.followers}</span>
              </div>
              <button className={`collab-follow-btn ${person.following ? 'following' : ''}`}>
                {person.following ? 'Following' : 'Follow'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Recommended For You Section */}
      <div className={`collab-section ${visibleItems >= 3 ? 'visible' : ''}`} style={{ transitionDelay: '400ms' }}>
        <div className="collab-section-header">
          <div className="collab-section-title">
            <Zap size={12} />
            <Sparkles size={12} className="collab-sparkle-icon" />
            <span>Recommended for You</span>
            <span className="collab-section-count">30</span>
          </div>
          <button className="collab-show-all">
            Show all <ChevronRight size={10} />
          </button>
        </div>
        <div className="collab-people-grid collab-ai-grid">
          {recommendedForYou.slice(0, 5).map((person, idx) => (
            <div 
              key={person.id} 
              className={`collab-person-card collab-person-card--ai ${idx < visibleItems ? 'visible' : ''}`}
              style={{ transitionDelay: `${(idx + 11) * 60}ms` }}
            >
              {person.aiPick && (
                <div className="collab-ai-pick-badge">
                  <Sparkles size={8} />
                  AI Pick
                </div>
              )}
              <div className="collab-person-avatar" style={{ background: avatarColors[person.color] }}>
                <span>{person.initials}</span>
                {person.verified && (
                  <div className="collab-verified-badge">
                    <CheckCircle2 size={8} />
                  </div>
                )}
              </div>
              <h4 className="collab-person-name">{person.name}</h4>
              <span className="collab-person-role">{person.role}</span>
              <span className="collab-person-field">{person.field}</span>
              <span className="collab-ai-reason">
                <Star size={8} />
                {person.reason}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render Discover Tab
  const renderDiscoverTab = () => (
    <div className="collab-discover">
      {/* Sub-tabs */}
      <div className="collab-discover-tabs">
        <button className="collab-discover-tab active">
          <Heart size={10} />
          For You
        </button>
        <button className="collab-discover-tab">
          <TrendingUp size={10} />
          Trending
        </button>
        <button className="collab-discover-tab">
          <Sparkles size={10} />
          AI Picks
          <span className="collab-ai-badge">AI</span>
        </button>
        <button className="collab-discover-tab">
          <Search size={10} />
          Search
        </button>
      </div>

      <div className="collab-discover-count">20 researchers found</div>

      {/* Discover Grid */}
      <div className="collab-discover-grid">
        {discoverResearchers.map((person, idx) => (
          <div 
            key={person.id} 
            className={`collab-discover-card ${idx < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${idx * 80}ms` }}
          >
            <div className="collab-discover-header">
              <div className="collab-discover-avatar" style={{ background: avatarColors[person.color] }}>
                <span>{person.initials}</span>
              </div>
              <div className="collab-discover-info">
                <div className="collab-discover-name-row">
                  <h4 className="collab-discover-name">{person.name}</h4>
                  {person.rating && (
                    <span className="collab-rating">
                      <Star size={9} />
                      {person.rating}
                    </span>
                  )}
                </div>
                <div className="collab-discover-role-row">
                  {person.verified && <CheckCircle2 size={9} className="collab-discover-verified" />}
                  <span className="collab-discover-role">{person.role}</span>
                </div>
                {person.field && <span className="collab-discover-field">{person.field}</span>}
              </div>
              <button className="collab-discover-menu">
                <Users size={12} />
              </button>
            </div>

            {person.openToCollab && (
              <span className="collab-open-badge">
                <Handshake size={9} />
                Open to Collaborate
              </span>
            )}

            <p className="collab-discover-bio">{person.bio}</p>

            <div className="collab-discover-tags">
              {person.tags.slice(0, 3).map((tag, tagIdx) => (
                <span key={tagIdx} className="collab-discover-tag">{tag}</span>
              ))}
            </div>

            <div className="collab-discover-stats">
              <span className="collab-stat">
                <Users size={10} />
                {person.followers}
              </span>
              <span className="collab-stat">
                <MessageCircle size={10} />
                0
              </span>
            </div>

            <div className="collab-discover-actions">
              <button className="collab-view-profile-btn">View Profile</button>
              <button className="collab-collaborate-btn">Collaborate</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Connections Tab
  const renderConnectionsTab = () => (
    <div className="collab-connections">
      {/* Sub-tabs */}
      <div className="collab-connections-tabs">
        <button className="collab-connections-tab">
          <Link2 size={10} />
          Collaborators
        </button>
        <button className="collab-connections-tab">
          <Heart size={10} />
          Followers
        </button>
        <button className="collab-connections-tab active">
          <UserCheck size={10} />
          Following
        </button>
        <button className="collab-connections-tab">
          <Sparkles size={10} />
          Suggested
          <span className="collab-ai-badge">AI</span>
        </button>
      </div>

      {/* Search */}
      <div className="collab-connections-search">
        <Search size={11} />
        <input type="text" placeholder="Search following..." readOnly />
      </div>

      <div className="collab-connections-count">5 following found</div>

      {/* Connections Grid */}
      <div className="collab-connections-grid">
        {connections.map((person, idx) => (
          <div 
            key={person.id} 
            className={`collab-connection-card ${idx < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${idx * 80}ms` }}
          >
            <div className="collab-connection-header">
              <div className="collab-connection-avatar" style={{ background: avatarColors[person.color] }}>
                <span>{person.initials}</span>
              </div>
              <div className="collab-connection-info">
                <div className="collab-connection-name-row">
                  <h4 className="collab-connection-name">{person.name}</h4>
                  {person.rating && (
                    <span className="collab-rating collab-rating--small">
                      <Star size={8} />
                      {person.rating}
                    </span>
                  )}
                </div>
                <div className="collab-connection-role-row">
                  {person.verified && <CheckCircle2 size={9} className="collab-connection-verified" />}
                  <span className="collab-connection-role">{person.role}</span>
                </div>
                {person.field && <span className="collab-connection-field">{person.field}</span>}
              </div>
              <button className="collab-connection-menu">
                <Users size={12} />
              </button>
            </div>

            <div className="collab-connection-stats">
              <Users size={10} />
              <span>{person.followers} followers</span>
            </div>

            <div className="collab-connection-actions">
              <button className="collab-view-profile-btn">View Profile</button>
              <button className="collab-message-btn">
                <MessageCircle size={10} />
                Message
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Requests Tab
  const renderRequestsTab = () => (
    <div className="collab-requests">
      {/* Sub-tabs */}
      <div className="collab-requests-tabs">
        <button className="collab-requests-tab">
          <Inbox size={10} />
          Incoming
          <span className="collab-incoming-count">3</span>
        </button>
        <button className="collab-requests-tab active">
          <History size={10} />
          History
        </button>
      </div>

      {/* Search */}
      <div className="collab-requests-search">
        <Search size={11} />
        <input type="text" placeholder="Search collaboration requests..." readOnly />
      </div>

      <div className="collab-requests-count">5 requests found</div>

      {/* Requests Grid */}
      <div className="collab-requests-grid">
        {requestsData.map((request, idx) => (
          <div 
            key={request.id} 
            className={`collab-request-card ${idx < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${idx * 80}ms` }}
          >
            <div className="collab-request-header">
              <div className="collab-request-avatar" style={{ background: avatarColors[request.color] }}>
                <span>{request.initials}</span>
              </div>
              <div className="collab-request-info">
                <div className="collab-request-name-row">
                  <h4 className="collab-request-name">{request.name}</h4>
                  {request.verified && (
                    <CheckCircle2 size={10} className="collab-request-verified" />
                  )}
                  <span className={`collab-request-type collab-request-type--${request.type}`}>
                    {request.type.toUpperCase()}
                  </span>
                </div>
                <span className="collab-request-role">{request.role}</span>
              </div>
              <span className={`collab-request-status collab-request-status--${request.status}`}>
                {request.status === 'pending' ? (
                  <><Clock size={9} /> Pending</>
                ) : (
                  <><CheckCircle2 size={9} /> Accepted</>
                )}
              </span>
            </div>

            {request.room && (
              <div className="collab-request-room">
                <Link2 size={9} />
                <span>{request.room}</span>
              </div>
            )}

            {request.message && (
              <p className="collab-request-message">"{request.message}"</p>
            )}

            {request.response && (
              <div className="collab-request-response">
                <span className="collab-response-label">RESPONSE:</span>
                <p>"{request.response}"</p>
              </div>
            )}

            <div className="collab-request-footer">
              <span className="collab-request-date">
                <Clock size={9} />
                {request.date}
              </span>
              <div className="collab-request-actions">
                {request.status === 'pending' && request.type === 'received' && (
                  <>
                    <button className="collab-accept-btn">Accept</button>
                    <button className="collab-decline-btn">Decline</button>
                  </>
                )}
                <button className="collab-request-view">
                  <Eye size={10} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render content based on active tab
  const renderContent = () => {
    switch (activeInteriorTab) {
      case 'discover':
        return renderDiscoverTab();
      case 'connections':
        return renderConnectionsTab();
      case 'requests':
        return renderRequestsTab();
      default:
        return renderNetworkTab();
    }
  };

  return (
    <div className="collab-mockup-content">
      {/* Header */}
      <div className="collab-mock-header">
        <div className="collab-header-icon">
          <Users size={14} />
        </div>
        <div className="collab-header-text">
          <h2 className="collab-header-title">Collaborators</h2>
          <p className="collab-header-subtitle">Your research network · 31 suggestions</p>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="collab-main-tabs">
        {mainTabs.map((tab) => (
          <button
            key={tab.key}
            className={`collab-main-tab ${activeInteriorTab === tab.key ? 'active' : ''}`}
            onClick={() => onTabClick(tab.key)}
          >
            <tab.icon size={12} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="collab-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default CollaboratorsMockupContent;
